import React from "react";
import { useSelector } from "react-redux";

export default function ReceiptPrintPage() {
  const billData = useSelector((state) => state.bill.data);
  const { branch, pos } = useSelector((state) => state.pos);
  const paymentResponse = useSelector((state) => state.bill.paymentResponse);

  /** Last payment snapshot in Redux; ignore when it is for a different bill (reprint/history screens). */
  const paymentExtrasForPrint = (() => {
    if (!paymentResponse) return null;
    if (!billData || typeof billData !== "object" || Array.isArray(billData))
      return paymentResponse;

    const receiptMismatch =
      Boolean(billData.receipt_no) &&
      paymentResponse.receiptNo != null &&
      String(paymentResponse.receiptNo) !== String(billData.receipt_no);

    const memberMismatch =
      billData.member_id != null &&
      paymentResponse.member?.id != null &&
      Number(paymentResponse.member.id) !== Number(billData.member_id);

    return receiptMismatch || memberMismatch ? null : paymentResponse;
  })();

  const formatNumber = (value) => {
    if (value === undefined || value === null || value === "-") return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getVatCalculator = () => {
    if (branch?.branch_info?.business?.settingVatCalculation?.code) {
      const vatCalculator =
        branch.branch_info.business.settingVatCalculation.code;
      if (vatCalculator === "INCLUDED") return "(Vat Included)";
      if (vatCalculator === "SEPARATE") return "(Vat Excluded)";
    }
    return "";
  };

  const getVatDisplayText = () => {
    if (branch?.branch_info?.business?.settingVatCalculation?.code) {
      const vatCalculator =
        branch.branch_info.business.settingVatCalculation.code;
      if (vatCalculator === "INCLUDED") return "(VAT Included)";
      if (vatCalculator === "SEPARATE") return "(VAT Excluded)";
    }
    return "";
  };

  const getBillItems = () => {
    if (!billData || !billData.billItems) {
      return [];
    }

    return billData.billItems.map((item) => {
      const orderItem = item.orderItem || {};
      const product = orderItem.product || {};
      const options = orderItem.orderProductOption || [];
      const specificOptions = orderItem.orderProductSpecificOption || [];
      const annotations = orderItem.orderProductAnnotation || [];

      return {
        id: item.id,
        quantity: item.quantity,
        name: product.name || "-",
        price: formatNumber(item.price),
        pricePerPiece: formatNumber(
          billData.order.order_type === "DELIVERY"
            ? (item.orderItem.product?.branchProduct &&
              item.orderItem.product?.branchProduct.length > 0 &&
              item.orderItem.product?.branchProduct[0].data?.price_delivery
                ? item.orderItem.product?.branchProduct[0].data?.price_delivery
                : item.orderItem.product?.branchProduct[0].data?.price ||
                  item.orderItem.product?.price_delivery) || 0
            : (item.orderItem.product?.branchProduct &&
              item.orderItem.product?.branchProduct.length > 0 &&
              item.orderItem.product?.branchProduct[0].data?.price
                ? item.orderItem.product?.branchProduct[0].data?.price
                : item.orderItem.product?.branchProduct[0].data?.price ||
                  item.orderItem.product?.price) || 0,
        ),
        total: item.price,
        options: [
          ...options.map((opt) => ({
            name: opt.productOptionItem?.name || "",
            price:
              parseFloat(opt.qty || 0) *
              parseFloat(opt.productOptionItem?.price || 0),
          })),
          ...specificOptions.map((opt) => ({
            name: opt.productSpecificOptionItem?.name || "",
            price:
              parseFloat(opt.qty || 0) *
              parseFloat(opt.productSpecificOptionItem?.price || 0),
          })),
        ],
        annotations: annotations.map(
          (ann) => ann.productAnnotationItem?.name || "",
        ),
      };
    });
  };

  const getBusinessInfo = () => {
    if (!billData?.order) {
      return {
        name: "-",
        company: "-",
        taxId: "-",
        branchName: "-",
        branchCode: "",
        logoUrl: "",
      };
    }

    const order = billData.order;
    const branchPos = order.branchPosSelling?.branchPos || {};
    const branchData = branchPos.branch || {};
    const business = branchData.business || {};
    const logoUrl = business.image_path || "";

    return {
      name: business.company_name || "-",
      company: business.company_name || "-",
      taxId: business.tax_number || "-",
      branchName: branchData.branch_name || "-",
      branchCode: branchData.code ? `(${branchData.code})` : "",
      logoUrl: logoUrl,
    };
  };

  const getTableDisplay = () => {
    if (!billData?.order) {
      return "-";
    }

    const order = billData.order;
    const orderType = order.order_type;

    if (orderType === "DINE_IN" && order.branchTable) {
      const zoneName = order.branchTable.branchTableZone?.name || "";
      const tableName = order.branchTable.name || "";
      return `${zoneName}/${tableName}`;
    } else if (orderType === "TAKEAWAY") {
      return "กลับบ้าน";
    } else if (orderType === "DELIVERY") {
      return "เดลิเวอรี่";
    }

    return "-";
  };

  const getReceiptDetails = () => {
    if (!billData) {
      return {
        date: "-",
        posId: "-",
        receiptNo: "-",
        staffName: "-",
        rdNumber: "-",
        tableInfo: "-",
      };
    }

    const user = billData.userBusinessGroup || {};
    const order = billData.order || {};

    const dateObj = new Date(billData.created_at || new Date());
    const formattedDate = `${dateObj.getDate().toString().padStart(2, "0")}/${(
      dateObj.getMonth() + 1
    )
      .toString()
      .padStart(
        2,
        "0",
      )}/${dateObj.getFullYear()} ${dateObj.getHours().toString().padStart(2, "0")}:${dateObj
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;

    const receiptNo =
      paymentExtrasForPrint?.receiptNo ||
      billData.receipt_no ||
      order.code ||
      "-";

    const staffName =
      user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.first_name || "-";

    return {
      date: formattedDate,
      posId: order.branchPosSelling?.branchPos?.pos_id || "-",
      receiptNo: receiptNo,
      staffName: staffName,
      rdNumber: "-",
      tableInfo: getTableDisplay(),
    };
  };

  const getTotals = () => {
    if (!billData) {
      return {
        subtotal: "0.00",
        discount: "0.00",
        pointDiscount: "0.00",
        serviceCharge: "0.00",
        netTotal: "0.00",
        vat: "0.00",
        totalAfterDiscount: "0.00",
        taxableAmount: "0.00",
        lossRounded: "0.00",
        finalAmount: "0.00",
        creditCardFee: "0.00",
        vatOnFee: "0.00",
      };
    }

    return {
      subtotal: formatNumber(billData.total_before_discount || 0),
      discount: formatNumber(billData.discount_amount || 0),
      pointDiscount: formatNumber(-(billData.discount_points || 0)),
      serviceCharge: formatNumber(billData.service_charge || 0),
      netTotal: formatNumber(billData.grand_total || 0),
      vat: formatNumber(billData.vat || 0),
      totalAfterDiscount: formatNumber(billData.total_after_discount || 0),
      taxableAmount: formatNumber(billData.taxable_amount || 0),
      lossRounded: formatNumber(billData.loss_rounded || 0),
      finalAmount: formatNumber(
        billData.final_profit_loss_rounded || billData.final_amount || 0,
      ),
      creditCardFee: formatNumber(billData.credit_card_fee || 0),
      vatOnFee: formatNumber(billData.vat_on_fee || 0),
    };
  };

  const getPaymentDetails = () => {
    if (!billData || !billData.payment || billData.payment.length === 0) {
      return [
        {
          method: "เงินสด",
          amount: "0.00",
        },
      ];
    }

    return billData.payment.map((payment) => {
      return {
        method: payment.paymentMethod?.name || "ไม่ระบุ",
        amount: formatNumber(payment.price || 0),
        paymentCode: payment.paymentMethod?.code || "",
        providers:
          payment.paymentMethod?.payment_providers &&
          payment.paymentMethod.payment_providers.map((provider) => ({
            amount: provider.amount || 0,
            name: provider.payment_provider_name || "",
          })),
      };
    });
  };

  const getMemberInfo = () => {
    const memberData =
      paymentExtrasForPrint?.member || billData?.member;
    const pointData =
      paymentExtrasForPrint?.pointData ||
      billData?.additional_data?.point ||
      {};

    if (!memberData) {
      return {
        id: "-",
        name: "-",
        phone: "-",
        pointsEarned: "0",
        pointsUsed: "0",
        pointsBalance: "0",
      };
    }

    return {
      id: memberData.code || "-",
      name:
        `${memberData.first_name || memberData.first_name_th || ""} ${
          memberData.last_name || memberData.last_name_th || ""
        }`.trim() || "-",
      phone: memberData.phone_number || "-",
      pointsEarned: pointData?.earned ? `+${pointData.earned}` : "0",
      pointsUsed: pointData?.spend || pointData?.used || "0",
      pointsBalance:
        pointData?.total || pointData?.balance || pointData?.remain || "0",
    };
  };

  const hasCreditCardPayment = () => {
    if (!billData || !billData.payment || billData.payment.length === 0) {
      return false;
    }

    return billData.payment.some(
      (payment) => payment.paymentMethod?.code === "CREDIT_CARD",
    );
  };

  const products = getBillItems();
  const business = getBusinessInfo();
  const receipt = getReceiptDetails();
  const totals = getTotals();
  const payments = getPaymentDetails();
  const member = getMemberInfo();
  const isCreditCardPayment = hasCreditCardPayment();

  const renderProductOptions = (options, quantity) => {
    if (!options || options.length === 0) return null;

    return options.map((option, index) => (
      <div key={index} className="flex w-full mt-1">
        <div className="w-1/4"></div>
        <div className="w-1/2 text-small text-xl">-&nbsp;{option.name}</div>
        <div className="w-1/4 text-small text-xl text-right">
          {Number(option.price || "0") > 0 && "+"}
          {Number(option.price || "0") > 0 &&
            formatNumber((option.price || 0) * quantity || 0)}
        </div>
        <div className="w-1/4 text-small text-xl text-right"></div>
      </div>
    ));
  };

  const renderProductAnnotations = (annotations) => {
    if (!annotations || annotations.length === 0) return null;

    return annotations.map((annotation, index) => (
      <div key={index} className="flex w-full mt-1">
        <div className="w-1/4"></div>
        <div className="w-1/2 text-small text-xl">-&nbsp;{annotation}</div>
        <div className="w-1/4"></div>
        <div className="w-1/4"></div>
      </div>
    ));
  };

  return (
    // <div className="bg-[#EFEFEF] pt-[4.00vw] pb-[12.79vw] min-h-screen print:block hidden">
    <div className="flex justify-center text-xl w-full leading-[1.8]">
      <div
        id="section-to-print-receipt-page"
        className="w-[29.49vw] bg-[#FFF] px-[1.70vw] py-[1.31vw] flex flex-col gap-[0.59vw]"
      >
        <div>
          <div className="text-center w-full flex flex-col items-center">
            {business.logoUrl && (
              <img
                className="object-cover aspect-square w-[7.71vw] h-fit logo-receipt"
                src={business.logoUrl}
                alt=""
              />
            )}

            <div>
              {business.name} - {business.branchName}
              {business.branchCode}
            </div>

            {business.taxId && (
              <div className="font-large">
                TAX ID : {business.taxId} {getVatDisplayText()}
              </div>
            )}

            <div className="font-semibold mt-[0.51vw]">
              {pos[0].tax_number
                ? "ใบกำกับภาษีอย่างย่อ / ใบเสร็จรับเงิน"
                : "ใบเสร็จรับเงิน"}
            </div>
          </div>

          <div className="flex flex-col gap-[0.39vw]">
            <div className="flex justify-between gap-[0.98vw]">
              <div className="whitespace-nowrap">วันที่ : {receipt.date}</div>
              <div className="text-right">POS# : {receipt.posId}</div>
            </div>
            <div className="flex justify-between gap-[0.98vw]">
              <div className="text-right">พนักงานขาย : {receipt.staffName}</div>
              <div className="text-right">โต๊ะ : {receipt.tableInfo}</div>
            </div>
            <div className="flex justify-between gap-[0.98vw]">
              <div className="whitespace-nowrap">
                เลขที่ : {receipt.receiptNo}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-[0.39vw]">
            <div className="mt-[0.78vw]">
              <div className="border-dashed border-t-[0.04vw] border-b-[0.04vw] border-black py-[0.39vw] flex">
                <div className="w-1/4 text-center font-medium">จำนวน</div>
                <div className="w-1/2 text-center font-medium">รายการ</div>
                <div className="w-1/4 text-right font-medium">หน่วยละ</div>
                <div className="w-1/4 text-right font-medium">รวมเงิน</div>
              </div>

              <div className="pt-[0.78vw] pb-[0.29vw] font-bold">
                จำนวนสินค้า :{" "}
                {products.reduce(
                  (sum, product) => sum + (parseInt(product.quantity) || 0),
                  0,
                )}
              </div>

              {products.map((product, index) => (
                <div key={product.id || index} className="mb-3">
                  <div className="flex pt-[0.29vw]">
                    <div className="w-1/4 text-center align-top leading-[1.45]">
                      {product.quantity}
                    </div>
                    <div className="w-1/2 align-top break-words leading-[1.45]">
                      {product.name}
                    </div>
                    <div className="w-1/4 text-right align-top leading-[1.45]">
                      {product.pricePerPiece || 0}
                    </div>
                    <div className="w-1/4 text-right align-top leading-[1.45]">
                      {formatNumber(Number(product.total || 0))}
                    </div>
                  </div>

                  {renderProductOptions(product.options, product.quantity)}
                  {renderProductAnnotations(product.annotations)}
                </div>
              ))}

              {paymentExtrasForPrint?.promotions &&
                paymentExtrasForPrint.promotions.length > 0 && (
                  <div className="mt-3">
                    <div className="py-[1.66vw] font-semibold">
                      โปรโมชั่นที่ได้รับ
                    </div>

                    {paymentExtrasForPrint.promotions.map((promo, index) => (
                      <div key={index} className="flex pt-[0.29vw]">
                        <div className="w-1/4 text-center align-top">
                          {promo.quantity || 1}
                        </div>
                        <div className="w-1/2 pl-[1.27vw] align-top">
                          {promo.name || "-"}
                        </div>
                        <div className="w-1/4 text-right align-top">
                          -{formatNumber(promo.price || 0)}
                        </div>
                        <div className="w-1/4 text-right align-top">
                          -{formatNumber(promo.total || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>

            <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.28vw] my-sp-13"></div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ยอดรวมก่อนส่วนลด</div>
              <div className="text-left">{totals.subtotal}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ส่วนลดท้ายบิล</div>
              <div className="text-left">
                {Number(totals.discount || "0") > 0 ? "-" : ""}{" "}
                {totals.discount}
              </div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ยอดขายหลังหักส่วนลด</div>
              <div className="text-left">{totals.totalAfterDiscount}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">
                Service Charge
                {billData?.service_charge_percent
                  ? ` (${billData.service_charge_percent}%)`
                  : ""}
              </div>
              <div className="text-left">{totals.serviceCharge}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">
                {Number(totals.vat) > 0
                  ? "มูลค่าสินค้ามีภาษี"
                  : "มูลค่าสินค้าไม่รวมภาษี"}
              </div>
              <div className="text-left">{totals.taxableAmount}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
              <div className="text-left">{totals.vat}</div>
            </div>

            <div className="flex justify-between w-full text-large text-2xl font-bold">
              <div className="text-[calc(3.7vw+2px)] whitespace-nowrap">
                ราคาสุทธิ
              </div>
              <div className="text-[calc(3.7vw+2px)] text-left">
                {totals.netTotal}
              </div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">จำนวนเงินปัดเศษ</div>
              <div className="text-left">{totals.lossRounded}</div>
            </div>

            <div className="flex justify-between w-full text-large text-2xl font-bold">
              <div className="text-[calc(3.7vw+2px)] whitespace-nowrap">
                จำนวนที่ต้องชำระ
              </div>
              <div className="text-left text-[calc(3.7vw+2px)]">
                {totals.finalAmount}
              </div>
            </div>
            <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.28vw] my-sp-13"></div>
            <div className="text-center">{getVatCalculator()}</div>
            <div className="text-[calc(3.7vw+2px)]">ช่องทางการชำระเงิน</div>
            {payments.map((payment, index) => (
              <div key={index} className="flex flex-col gap-[0.98vw]">
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">{payment.method}</div>
                  <div className="text-right">
                    {Array.isArray(payment.providers) &&
                    payment.providers.length > 0
                      ? ""
                      : payment.amount}
                  </div>
                </div>
                {Array.isArray(payment.providers) &&
                  payment.providers.map((provider, providerIndex) => (
                    <div
                      className="flex justify-between ml-2"
                      key={`provider-${providerIndex}`}
                    >
                      <div className="whitespace-nowrap pl-[1.5vw]">
                        - {provider.name}
                      </div>
                      <div className="text-right">
                        {formatNumber(provider.amount)}
                      </div>
                    </div>
                  ))}
              </div>
            ))}

            {isCreditCardPayment && (
              <>
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">
                    ค่าธรรมเนียมบัตรเครดิต{" "}
                    {billData.credit_card_fee_percent
                      ? `(${billData.credit_card_fee_percent}%)`
                      : ""}
                  </div>
                  <div className="text-right">
                    {formatNumber(totals.creditCardFee)}
                  </div>
                </div>
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
                  <div className="text-right">
                    {formatNumber(totals.vatOnFee)}
                  </div>
                </div>
              </>
            )}

            {paymentExtrasForPrint?.cash_out &&
              parseFloat(paymentExtrasForPrint.cash_out) > 0 && (
                <div className="flex justify-between gap-[0.98vw]">
                  <div className="whitespace-nowrap">เงินทอน</div>
                  <div className="text-right">
                    {formatNumber(paymentExtrasForPrint.cash_out)}
                  </div>
                </div>
              )}

            {paymentExtrasForPrint?.member || billData?.member ? (
              <>
                <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>

                <div className="text-center">Member : {member.id}</div>
                <div className="flex justify-between gap-[0.98vw] mt-[1.66vw]">
                  <div className="whitespace-nowrap">คุณ : {member.name}</div>
                  <div className="text-right">เบอร์ : {member.phone}</div>
                </div>

                <div className="flex mt-[0.78vw]">
                  <div className="w-1/4"></div>
                  <div className="w-1/4 text-right">ได้รับ</div>
                  <div className="w-1/4 text-right">ใช้ไป</div>
                  <div className="w-1/4 text-right">คงเหลือ</div>
                </div>

                <div className="flex">
                  <div className="w-1/4">คะแนน</div>
                  <div className="w-1/4 text-right">{member.pointsEarned}</div>
                  <div className="w-1/4 text-right">{member.pointsUsed}</div>
                  <div className="w-1/4 text-right">{member.pointsBalance}</div>
                </div>

                <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
              </>
            ) : (
              <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
            )}

            <div className="text-center">THANK YOU</div>
            <div className="text-center">ขอบคุณที่ใช้บริการ</div>
            <div className="text-center" style={{ fontSize: "44px" }}>
              {billData.order.remark ? `(${billData.order.remark})` : ""}{" "}
            </div>

            <div className="py-7"></div>
          </div>
        </div>
      </div>
    </div>
    // </div>
  );
}
